import { useState } from "preact/hooks";
import { api } from "../api/client.ts";
import type { Member, OrgOut, PersonalAgent, Role, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { canAddAgents, manages, rolesFor, setUpByName } from "../lib/agents.ts";
import { INVITE, useAsked } from "../lib/asked.ts";
import { usageText } from "../lib/billing.ts";
import { useMembers, useOrg, usePersonalAgents } from "../lib/data.ts";
import { formatInstant, formatRelative } from "../lib/format.ts";
import { orgApi, spaceUrl } from "../lib/paths.ts";
import { displayName } from "../lib/people.ts";
import { type PersonalAction, personalAction, personalOf } from "../lib/personalAgents.ts";
import { useAction } from "../lib/resource.ts";
import { atLeast, grantable, roleOptions } from "../lib/roles.ts";
import { navigate } from "../lib/router.ts";
import { readOnly } from "../lib/standing.ts";
import { me, type PageProps, reloadAndGoHome } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { useId } from "../lib/uid.ts";
import { Avatar } from "../ui/Avatar.tsx";
import { Button, Link } from "../ui/Button.tsx";
import { ConfirmDialog, Dialog, DialogActions } from "../ui/Dialog.tsx";
import { Field, Form, Input, Select } from "../ui/form.tsx";
import { AgentIcon, InviteIcon, MoreIcon, SendIcon } from "../ui/icons.tsx";
import { McpIcon } from "../ui/McpIcon.tsx";
import { Menu, type MenuEntry } from "../ui/Menu.tsx";
import { Async, Badge } from "../ui/misc.tsx";
import { buttonClass, iconButtonClass, LINK } from "../ui/styles.ts";
import { Table, Td, Th } from "../ui/Table.tsx";
import { EditAgent } from "./settings/agentForm.tsx";
import { ConnectAgent } from "./settings/connect.tsx";
import { SettingsNav } from "./settings/nav.tsx";
import { PersonalDialogs } from "./settings/personalEntry.tsx";

/** Inviting a person (by email), with their role (a dialog's). (Agents are added with their key: `ConnectAgent`.) */
function InvitePerson({ space, onDone, onCancel }: { space: SpaceOut; onDone: () => void; onCancel: () => void }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [add, busy] = useAction(async () => {
    await api.post(orgApi(space, "/members"), { email, role });
    toast(t("members.added"));
    onDone();
  });
  return (
    <Form class="flex flex-col gap-4" onSubmit={add}>
      <Field label={t("members.email")}>
        <Input type="email" value={email} onValue={setEmail} required={true} autofocus={true} />
      </Field>
      <Field label={t("members.role")}>
        <Select value={role} onValue={(v) => setRole(v as Role)} options={roleOptions(grantable(space.role))} />
      </Field>
      <DialogActions>
        <Button answer="cancel" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" answerShows="icon" variant="primary" submit={true} busy={busy}>
          <SendIcon />
          {t("members.invite")}
        </Button>
      </DialogActions>
    </Form>
  );
}

/** When an invitation may be sent again (none: now). */
const resendWait = (m: Member): string | null => {
  const at = m.invitation?.resend_at;
  return at && Date.parse(at) > Date.now() ? at : null;
};

/** A person who hasn't signed in yet: where their invitation email stands (and from when another may be sent). */
function invitationText(m: Member): string | null {
  const inv = m.invitation;
  if (!inv) {
    return null;
  }
  let status = t("members.invitation.notSent");
  if (inv.status === "sending") {
    status = t("members.invitation.sending");
  } else if (inv.status === "sent" && inv.at) {
    status = t("members.invitation.sent", { when: formatRelative(inv.at) });
  }
  const wait = resendWait(m);
  return wait
    ? `${status} · ${t("members.invitation.wait", { time: formatInstant(wait, { timeStyle: "short" }) })}`
    : status;
}

interface RowProps {
  space: SpaceOut;
  m: Member;
  /** Everyone (who set an agent up, whom it may work for). */
  members: readonly Member[];
  onChange: () => void;
  /** The org's personal agents. */
  links: PersonalAgent[] | undefined;
  onLinksChange: () => void;
  /** Connect this agent (its key and setup). */
  onConnect: (agent: string) => void;
  /** Change this agent's name and picture. */
  onEdit: (agent: Member) => void;
}

/**
 * A member: who (with their email, or an agent's person and who set it up: words only), their kind and role; at most
 * one action in sight (connecting an agent, else their review queue) and the rest in a menu, removing last.
 */
function MemberRow({ space, m, members, onChange, links, onLinksChange, onConnect, onEdit }: RowProps) {
  const self = m.id === me.value?.id;
  const managed = manages(m, me.value?.id, space.role);
  const bot = m.kind !== "human";
  const [confirm, setConfirm] = useState(false);
  const [setRole] = useAction(async (role: string) => {
    await api.patch(orgApi(space, `/members/${m.id}`), { role });
    onChange();
  });
  const [remove, removing] = useAction(async () => {
    await api.del(orgApi(space, `/members/${m.id}`));
    if (self) {
      await reloadAndGoHome();
    } else {
      onChange();
    }
  });
  const [resend] = useAction(async () => {
    await api.post(orgApi(space, `/members/${m.id}/invitation`));
    toast(t("members.invitation.resent"));
    onChange();
  });
  const [personalOpen, setPersonalOpen] = useState<PersonalAction | null>(null);
  const personal = personalAction(m, { members, links, me: me.value?.id, role: space.role });
  const personalLabel = {
    remove: t("personal.remove"),
    make: atLeast(space.role, "admin") ? t("personal.make") : t("personal.makeMine"),
  };
  const queue = `${spaceUrl(space, "/queue")}?reviewer=${m.id}`;
  // Admins connect any agent or integration (an admin one too); others those they set up.
  const connects = bot && (managed || atLeast(space.role, "admin"));
  const leave = self ? t("members.leave") : t("common.remove");
  const items: MenuEntry[] = [
    ...(bot && managed ? [{ label: t("members.edit"), onSelect: () => onEdit(m) }] : []),
    ...(personal
      ? [{ label: personalLabel[personal], onSelect: () => setPersonalOpen(personal), danger: personal === "remove" }]
      : []),
    ...(connects ? [{ label: t("members.queue"), onSelect: () => navigate(queue) }] : []),
    ...(m.invitation && managed && !resendWait(m)
      ? [{ label: t("members.invitation.resend"), onSelect: () => resend() }]
      : []),
    ...(managed || self ? [{ label: leave, onSelect: () => setConfirm(true), danger: true, separated: true }] : []),
  ];
  // Words under the name: an email, an invitation's state, whom an agent works for and who set it up.
  const by = setUpByName(m, members);
  const link = m.kind === "agent" ? personalOf(links, m.id) : undefined;
  const lines = [
    m.email,
    invitationText(m),
    link ? t("personal.of", { name: link.person_name }) : null,
    by ? t("members.setUpBy", { name: by }) : null,
  ].filter((x): x is string => Boolean(x));
  return (
    <tr>
      <Td>
        <span class="flex items-center gap-3">
          <Avatar id={m.id} name={m.name} kind={m.kind} picture={m.avatar ?? null} size={32} />
          <span class="min-w-0">
            <span class="font-medium">{displayName(m.id, m.name)}</span>
            {lines.map((l) => (
              <span key={l} class="block text-xs text-zinc-500">
                {l}
              </span>
            ))}
          </span>
        </span>
      </Td>
      <Td label={t("members.kind")}>
        <Badge>{t(`userKind.${m.kind}`)}</Badge>
      </Td>
      <Td label={t("members.role")}>
        {managed ? (
          <Select
            aria-label={t("members.roleOf", { name: m.name })}
            value={m.role}
            onValue={setRole}
            options={roleOptions(rolesFor(m, members, space.role))}
          />
        ) : (
          t(`role.${m.role}`)
        )}
      </Td>
      <Td align="right" label="">
        <span class="flex items-center justify-end gap-1 whitespace-nowrap">
          {connects ? (
            <Button size="sm" onClick={() => onConnect(m.id)}>
              <McpIcon size={14} />
              {t("members.connect")}
            </Button>
          ) : (
            <Link href={queue} class={buttonClass("ghost", "sm")}>
              {t("members.queue")}
            </Link>
          )}
          {items.length > 0 ? (
            <Menu
              aria-label={t("members.more", { name: m.name })}
              label={<MoreIcon />}
              class={iconButtonClass("sm")}
              items={items}
            />
          ) : null}
        </span>
        <ConfirmDialog
          open={confirm}
          onClose={() => setConfirm(false)}
          title={self ? t("members.leave") : t("members.removeTitle")}
          message={t("members.removeMessage", { name: m.name })}
          confirmLabel={leave}
          onConfirm={() => (removing ? undefined : remove())}
        />
        {m.kind === "agent" ? (
          <PersonalDialogs
            open={personalOpen}
            onClose={() => setPersonalOpen(null)}
            org={space.org}
            role={space.role}
            agent={m}
            members={members}
            links={links}
            onChange={onLinksChange}
          />
        ) : null}
      </Td>
    </tr>
  );
}

/**
 * The plan's people beside the buttons: "3 / 5 people" (or "3 / Unlimited"), and a way to the plan; in amber when it's
 * full (then `full` explains, under the row, why inviting is off).
 */
function Seats({ space, org, people }: { space: SpaceOut; org: OrgOut; people: number }) {
  const limit = org.standing.limits?.people;
  const full = typeof limit === "number" && people >= limit;
  return (
    <p
      class={`flex w-full flex-wrap items-baseline gap-x-2 text-sm sm:ml-auto sm:w-auto sm:justify-end ${full ? "text-amber-800" : "text-zinc-600"}`}
      title={usageText(people, limit)}
    >
      <span class="tabular-nums">
        {typeof limit === "number"
          ? t("members.seats", { count: limit, used: people })
          : t("members.seatsUnlimited", { count: people })}
      </span>
      <Link href={spaceUrl(space, "/settings/billing")} class={`${LINK} text-xs`}>
        {t("billing.see")}
      </Link>
    </p>
  );
}

/** Whether the org's plan has room for no more people. */
const planFull = (org: OrgOut | undefined, people: number | undefined): boolean => {
  const limit = org?.standing.limits?.people;
  return typeof limit === "number" && people !== undefined && people >= limit;
};

/** What the page has open: inviting a person, adding an agent, or connecting one (for a given agent, or not). */
type Open = { kind: "person" } | { kind: "agent" } | { kind: "connect"; agent: string | null } | null;

/**
 * The organization's members (people, agents, integrations) and their roles; inviting a person, adding an agent and
 * connecting one (its key and setup) in dialogs.
 */
export function Members({ space }: PageProps) {
  const members = useMembers(space);
  const org = useOrg(space);
  const links = usePersonalAgents(space.org);
  const [open, setOpen] = useState<Open>(null);
  const [editing, setEditing] = useState<Member | null>(null);
  const close = () => setOpen(null);
  const admin = atLeast(space.role, "admin");
  const addsAgents = canAddAgents(space.role, org.data?.editors_add_agents ?? false);
  const locked = readOnly(space);
  // People count toward the plan (agents and integrations don't): counted from the list, so it follows changes here.
  const people = members.data?.filter((m) => m.kind === "human").length;
  const full = planFull(org.data, people);
  const fullId = useId();
  const hint = locked ? t("plan.readOnly") : undefined;
  const inviteHint = hint ?? (full ? t("members.seatsFull") : undefined);
  useAsked(INVITE, () => admin && !locked && !full && setOpen({ kind: "person" }));
  return (
    <>
      <SettingsNav space={space} tab="/members" />
      <div class="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        <div class="flex flex-wrap gap-2">
          {admin ? (
            <Button
              variant="primary"
              disabled={locked || full}
              title={inviteHint}
              aria-describedby={full ? fullId : undefined}
              onClick={() => setOpen({ kind: "person" })}
            >
              <InviteIcon />
              {t("members.invite")}
            </Button>
          ) : null}
          {addsAgents ? (
            <Button disabled={locked} title={hint} onClick={() => setOpen({ kind: "agent" })}>
              <AgentIcon />
              {admin ? t("members.addAgent") : t("members.addAgentOnly")}
            </Button>
          ) : null}
          {admin ? null : (
            // Connecting an agent with a key of their own (or of an agent they set up).
            <Button onClick={() => setOpen({ kind: "connect", agent: null })}>
              <McpIcon size={16} />
              {t("connect.title")}
            </Button>
          )}
        </div>
        {org.data && people !== undefined ? <Seats space={space} org={org.data} people={people} /> : null}
        {admin && full ? (
          <p id={fullId} class="w-full text-xs text-amber-800">
            {t("members.seatsFull")}
          </p>
        ) : null}
      </div>
      <Async r={members}>
        {(list) => (
          <Table stack={true}>
            <thead class="bg-zinc-50">
              <tr>
                <Th>{t("members.name")}</Th>
                <Th>{t("members.kind")}</Th>
                <Th>{t("members.role")}</Th>
                <Th />
              </tr>
            </thead>
            <tbody class="divide-y divide-zinc-100">
              {list.map((m) => (
                <MemberRow
                  key={m.id}
                  space={space}
                  m={m}
                  members={list}
                  onChange={members.reload}
                  links={links.data}
                  onLinksChange={links.reload}
                  onConnect={(agent) => setOpen({ kind: "connect", agent })}
                  onEdit={setEditing}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Async>
      <Dialog open={open?.kind === "person"} onClose={close} title={t("members.invite")}>
        <InvitePerson
          space={space}
          onCancel={close}
          onDone={() => {
            close();
            members.reload();
          }}
        />
      </Dialog>
      <EditAgent space={space} member={editing} onClose={() => setEditing(null)} onDone={members.reload} />
      <ConnectAgent
        space={space}
        open={open?.kind === "agent" || open?.kind === "connect"}
        agent={open?.kind === "connect" ? open.agent : null}
        adding={open?.kind === "agent"}
        members={members.data}
        onChange={() => {
          members.reload();
          links.reload();
        }}
        onClose={close}
      />
    </>
  );
}
